import Breadcrumb from '@components/common/breadcrumb'
import Ratings from '@components/common/ratings'
import StudentCountriesOverview from '@components/home/student-countries-overview'
import { COURSES_BREADCRUMB } from '@lib/data/breadcrumb'
import { COURSE_FIRST_FOLD, COURSE_TITLES } from '@lib/data/course-landing-page'
import { CourseNameType } from '@lib/types/course-landing-page'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import { Activity, Triangle, Youtube } from 'react-feather'

const CourseCard = dynamic(() =>
  import('@components/course-landing-page/course-card')
)
const PlatformCourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)
const CourseHeaderStats = dynamic(() =>
  import('@components/course-landing-page/course-header-stats')
)

type Props = {
  shouldHideCourseCard?: boolean
  courseName: CourseNameType
  courseContentText?: string
  ishideStarRating?: boolean
  ishideDownloadCurriculum?: boolean
  bgImageUrl?: string
  urlQuery?: string
  isHpePage?: boolean
  customPoints?: string
  isPlatformPage?: boolean
}
const FirstFold = ({
  shouldHideCourseCard,
  courseName,
  courseContentText,
  ishideStarRating,
  ishideDownloadCurriculum,
  bgImageUrl,
  urlQuery,
  isHpePage,
  customPoints,
  isPlatformPage,
}: Props) => {
  const coursesData = COURSE_FIRST_FOLD[courseName]
  const breadcrumb = COURSES_BREADCRUMB[courseName]
  const [showMore, setShowMore] = useState(false)

  function toggleText() {
    setShowMore((s) => !s)
  }
  return (
    <>
      <div
        style={
          bgImageUrl && {
            backgroundImage: `url(${bgImageUrl})`,
            backgroundSize: 'cover',
          }
        }
        className="relative justify-between lg:px-6 hidden md:flex hero-gradient-white-orange"
      >
        <div className="container mx-auto flex justify-between flex-col space-x-4 text-grey z-5 lg:flex-row lg:items-center py-4 md:py-10 px-5 lg:px-0 xl:px-10">
          <div className="md:w-4/5 md:pr-4 lg:max-w-xl xl:max-w-3xl">
            <div className="mb-3">
              <Breadcrumb items={breadcrumb} isInCoursePage />
            </div>

            <h1 className="font-600 text-4xl text-left max-w-2xl leading-tight">
              {coursesData.name}
            </h1>
            <p className="font-600 text-lg text-left mb-0 mt-4 max-w-2xl">
              <span>
                {showMore || coursesData.description.split(' ').length <= 55
                  ? coursesData.description
                  : coursesData.description.split(' ').slice(0, 55).join(' ') +
                    '...'}
              </span>
              {coursesData.description.split(' ').length > 55 && (
                <span>
                  <button
                    className="text-orange inline pl-1 cursor-pointer focus:outline-none hover:underline"
                    onClick={toggleText}
                  >
                    Read {showMore ? 'Less' : 'More'}
                  </button>
                </span>
              )}
            </p>
            {isHpePage && (
              <div className="text-left my-8 mb-3 max-w-4xl flex flex-wrap space-x-3">
                <div className="flex items-center">
                  <Youtube
                    width={18}
                    height={18}
                    className=" text-orange mr-1"
                  />
                  12 hours of live session
                </div>
                <div className="flex items-center">
                  <Activity
                    width={18}
                    height={18}
                    className=" text-orange mr-1"
                  />
                  {courseContentText}
                </div>
                <div className="flex items-center">
                  <Triangle
                    width={18}
                    height={18}
                    className=" text-orange mr-1"
                  />
                  quizzes & activities
                </div>
              </div>
            )}
            {coursesData.firstFoldTable && (
              <div className="flex space-x-4 items-start mb-1 text-sm">
                {coursesData.firstFoldTable.map((item, index) => (
                  <>
                    <div key={index}>
                      <p className="text-left mt-6 text-orange-600">
                        {item.icon}

                        {item.title}
                      </p>
                      <p
                        className="font-600 text-left mb-3"
                        style={{ maxWidth: '85px' }}
                      >
                        {item.description}
                      </p>
                    </div>
                    {index < coursesData.firstFoldTable.length - 1 && (
                      <span
                        className="bg-orange-600 bg-opacity-50 h-10 mt-6"
                        style={{ width: '1px' }}
                      />
                    )}
                  </>
                ))}
              </div>
            )}
            {!ishideStarRating && (
              <div className="flex flex-col md:flex-row md:space-x-8 space-y-5 md:space-y-0 justify-center lg:justify-start">
                <StudentCountriesOverview isTextWhite={false} />
                <Ratings isDarkText />
              </div>
            )}
          </div>
          <div className="ml-4 relative w-80 hidden lg:block">
            <div className="pb-5 absolute top-0 -mt-32 right-0">
              {!shouldHideCourseCard && (
                <>
                  {!isPlatformPage && (
                    <CourseCard
                      courseName={courseName}
                      ishideDownloadCurriculum={ishideDownloadCurriculum}
                      ishideStarRating={ishideStarRating}
                      urlQuery={urlQuery}
                      isHpePage={isHpePage}
                      customPoints={customPoints}
                    />
                  )}
                  {isPlatformPage && (
                    <PlatformCourseCard
                      courseName={courseName}
                      ishideDownloadCurriculum={ishideDownloadCurriculum}
                      ishideStarRating={ishideStarRating}
                      urlQuery={urlQuery}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="md:hidden">
        <div className="font-600 my-3 mx-3">
          <Breadcrumb items={breadcrumb} isInCoursePage />
        </div>
        {!isPlatformPage && (
          <CourseCard
            courseName={courseName}
            ishideDownloadCurriculum={ishideDownloadCurriculum}
            ishideStarRating={ishideStarRating}
            urlQuery={urlQuery}
            isHpePage={isHpePage}
            customPoints={customPoints}
          />
        )}
        {isPlatformPage && (
          <PlatformCourseCard
            courseName={courseName}
            ishideDownloadCurriculum={ishideDownloadCurriculum}
            ishideStarRating={ishideStarRating}
            urlQuery={urlQuery}
          />
        )}
      </div>
      {shouldHideCourseCard && (
        <CourseHeaderStats
          courseName={COURSE_TITLES[courseName]}
          ishideStarRating={ishideStarRating}
        />
      )}
    </>
  )
}

export default FirstFold
